import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  createCtcLevel,
  deleteCtcLevel,
  getCtcLevels,
  updateCtcLevel,
} from '../services/ctc-level.service';
import {
  CtcLevelInsertInput,
  CtcLevelUpdateInput,
  GetCtcLevelsParams,
} from '../types/ctc-level.types';

/** The employee wizard's CTC dropdown reads the same collection under its own key, so a change
 * here must refresh it too — otherwise it serves the stale 30-minute cache. */
const invalidateCtcLevelQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['hr-ctc-levels'] });
  queryClient.invalidateQueries({ queryKey: ['hr-basic-info-ctc-levels'] });
};

export const useGetCtcLevels = (params: GetCtcLevelsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-ctc-levels', params],
    queryFn: () => getCtcLevels(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateCtcLevel = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: CtcLevelInsertInput) => createCtcLevel(input),
    onSuccess: () => {
      invalidateCtcLevelQueries(queryClient);
      toast({ title: t('CTC_LEVEL_CREATED') });
    },
  });
};

export const useUpdateCtcLevel = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: CtcLevelUpdateInput }) => updateCtcLevel(params),
    onSuccess: () => {
      invalidateCtcLevelQueries(queryClient);
      toast({ title: t('CTC_LEVEL_UPDATED') });
    },
  });
};

export const useDeleteCtcLevel = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deleteCtcLevel(itemId),
    onSuccess: () => {
      invalidateCtcLevelQueries(queryClient);
      toast({ title: t('CTC_LEVEL_DELETED') });
    },
  });
};
